class MadVideo {
  /**
   * Konstruktor der MadVideo Klasse.
   * @param {jQuery} $container - Das jQuery-Element des Video-Containers.
   */
  constructor($container) {
    this.$container = $container;
    this.videoUrl = this.$container.data("video-url");
    this.autoplay = this.$container.data("video-autoplay") === true;

    // Definieren der jQuery-Elemente als Klassenattribute
    this.$overlay = this.$container.find(".mad-video__overlay");
    this.$playButton = this.$container.find(".mad-video__play-button");
    this.$videoContainer = this.$container.find(".mad-video");
    this.isPlaying = false;
    this.isVideoConsentGiven = false;

    this.init();
  }

  /**
   * Initialisierung der Video-Container.
   * Setzt Event-Listener und lädt das Thumbnail.
   */
  init() {
    const type = this.getVideoTypeFromUrl();
    this.isVideoConsentGiven = this.isLocalDomainVideo(this.videoUrl);
    if (type === "default") {
      console.info(
        "Unsupported video type. Only YouTube and Vimeo are supported."
      );

      // Event-Listener für den Play-Button hinzufügen
      this.$playButton.on("click", (e) => {
        e.preventDefault();
        this.playVideo();
      });
    } else {
      // Check Video Consent
      this.doVideoConsent();

      // Event-Listener für den Play-Button hinzufügen
      this.$playButton.on("click", (e) => {
        e.preventDefault();

        jQuery(document).trigger("madRequestVideoConsent", {
          type: type, // returns 'vimeo', 'youtube', etc.
        });
        this.playVideo();
      });
    }

    // Thumbnail laden und anzeigen
    this.loadThumbnail();
  }

  /**
   * Checks if a video URL is from the same domain or subdomain as the current page.
   * @param {string} url - The video URL to check.
   * @returns {boolean} - True if the video is from the same domain or subdomain.
   */
  isLocalDomainVideo(url) {
    try {
      if (!url) return false;
      
      if (url.startsWith('/')) {
        return true;
      }
      
      const siteDomain = window.location.hostname;
      
      const videoUrlObj = new URL(url);
      const videoDomain = videoUrlObj.hostname;
      
      if (videoDomain === siteDomain) {
        return true;
      }
      
      const mainDomainParts = siteDomain.split('.');
      const mainDomain = mainDomainParts.slice(mainDomainParts.length - 2).join('.');
      
      return videoDomain.endsWith('.' + mainDomain);
    } catch (e) {
      console.error('Error checking domain:', e);
      return false;
    }
  }

  /**
   * Checks if Video Consent is given.
   * If given, triggers the event to play the video.
   * If not given, sets up the event listener for consent.
   * @returns {void}
   */
  doVideoConsent() {
    const type = this.getVideoTypeFromUrl();

    jQuery(document).on(`madVideoConsentGiven:${type}`, (event, data) => {
      this.isVideoConsentGiven = true;
      this.playVideo();
    });

    if (window.madVideoConsentGiven && window.madVideoConsentGiven[type]) {
      jQuery(document).trigger(`madVideoConsentGiven:${type}`, {
        videoConsent: true,
      });
    }

    if (this.isVideoConsentGiven) {
      if (this.autoplay) {
        this.playVideo();
      }
    }
  }

  /**
   * Determines the video type based on the video URL.
   * @returns {string} One of: 'youtube', 'vimeo', 'hls', 'default'
   */
  getVideoTypeFromUrl() {
    if (this.extractVimeoId(this.videoUrl)) return "vimeo";
    if (this.extractYoutubeId(this.videoUrl)) return "youtube";
    if (this.isHlsUrl(this.videoUrl)) return "hls";
    return "default";
  }

  /**
   * Checks if a URL is an HLS stream (.m3u8).
   * @param {string} url - The URL to check.
   * @returns {boolean} - True if the URL is an HLS stream.
   */
  isHlsUrl(url) {
    if (!url) return false;
    const hlsRegex = /\.m3u8(\?.*)?$/i;
    return hlsRegex.test(url);
  }

  /**
   * Ruft das Thumbnail eines Vimeo-Videos anhand seiner ID ab.
   * @param {number|string} videoId - Die ID des Vimeo-Videos.
   * @returns {Promise<string>} - Ein Promise, das die Thumbnail-URL zurückgibt.
   */
  async fetchVimeoThumbnail(videoId) {
    const url = `https://vimeo.com/api/v2/video/${videoId}.json`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Netzwerkantwort war nicht ok: ${response.statusText}`);
      }

      const data = await response.json();

      if (data && data.length > 0 && data[0].thumbnail_large) {
        return data[0].thumbnail_large;
      } else {
        throw new Error("Thumbnail nicht gefunden.");
      }
    } catch (error) {
      console.error("Fehler beim Abrufen des Thumbnails:", error);
      throw error;
    }
  }

  /**
   * Lädt das Thumbnail für das Video.
   * Unterstützt Vimeo und YouTube.
   */
  async loadThumbnail() {
    const vimeoId = this.extractVimeoId(this.videoUrl);
    const youtubeId = this.extractYoutubeId(this.videoUrl);

    if (this.isVideoConsentGiven) {
      if (vimeoId) {
        try {
          const thumbnailUrl = await this.fetchVimeoThumbnail(vimeoId);
          this.$overlay.css("background-image", `url(${thumbnailUrl})`);
        } catch (error) {
          console.error("Fehler beim Laden des Thumbnails:", error);
        }
      } else if (youtubeId) {
        const thumbnailUrl = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
        this.$overlay.css("background-image", `url(${thumbnailUrl})`);
      } else {
        console.error("Ungültige Video-URL oder ID.");
      }
    }
  }

  /**
   * Extrahiert die Vimeo ID aus der Video-URL.
   * @param {string} url - Die URL des Vimeo-Videos.
   * @returns {string|null} - Die extrahierte Vimeo ID oder null, wenn nicht gefunden.
   */
  extractVimeoId(url) {
    const vimeoRegex =
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    const match = url.match(vimeoRegex);
    return match ? match[1] : null;
  }

  /**
   * Extrahiert die YouTube ID aus der Video-URL.
   * @param {string} url - Die URL des YouTube-Videos.
   * @returns {string|null} - Die extrahierte YouTube ID oder null, wenn nicht gefunden.
   */
  extractYoutubeId(url) {
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(youtubeRegex);
    return match ? match[1] : null;
  }

  /**
   * Methode zum Abspielen des Videos.
   * Versteckt das Overlay und lädt das Video.
   */
  playVideo() {
    if (this.isPlaying) return;
    this.isPlaying = true;
    
    this.$overlay.fadeOut(300, async () => {
      const vimeoId = this.extractVimeoId(this.videoUrl);
      const youtubeId = this.extractYoutubeId(this.videoUrl);
      const isHls = this.isHlsUrl(this.videoUrl);
  
      if (youtubeId) {
        const uniqueId = `player-${youtubeId}-${Math.random().toString(36).substr(2, 5)}`;
        
        this.$videoContainer.html(
          `<div id="${uniqueId}" data-plyr-provider="youtube" data-plyr-embed-id="${youtubeId}"></div>`
        );
        try {
          const player = new Plyr('#' + uniqueId, {
            iconUrl: window.madPlyrIconUrl,
            storage: { enabled: false },
            autoplay: this.autoplay,
            muted: this.autoplay
          });
          this.$container.addClass("active");
        } catch (error) {
          console.error("Fehler beim Laden des YouTube-Videos:", error);
          this.$overlay.fadeIn(300);
          this.isPlaying = false;
        }
      } else if (vimeoId || isHls) {
        if (window.VideoLoader) {
          try {
            const videoLoader = new VideoLoader();
            const player = await videoLoader.load(
              this.$videoContainer,
              this.videoUrl,
              this.autoplay === true
            );
            this.$container.addClass("active");
          } catch (error) {
            console.error("Fehler beim Laden des Videos:", error);
            this.$overlay.fadeIn(300);
            this.isPlaying = false;
          }
        } else {
          console.error("VideoLoader ist nicht verfügbar.");
          this.$overlay.fadeIn(300);
          this.isPlaying = false;
        }
      } else {
        console.error("Ungültige Video-URL oder ID.");
        this.$overlay.fadeIn(300);
        this.isPlaying = false;
      }
    });
  }
}

jQuery(document).ready(function () {
  jQuery(".mad-video__container").each(function () {
    new MadVideo(jQuery(this));
  });
});
